import { getName } from "country-list";
import { calculateFinancialSupport, toCurrency } from "../../../helpers";
import {
  REQUIRED_DOCS,
  SECONDARY_SCHOOL_LEVEL,
  SECONDARY_SCHOOL_TYPE,
} from "../../academic/constants";
import { IRequiredDoc } from "../../academic/types";
import { FilterFormType, INTAKES_MAP, PROGRAM_TYPES } from "../constants";
import {
  CalcFeeWithScholarshipAndFinancialAid,
  IAccommodationFee,
  SumTuitionAndOtherFeesSupports,
} from "../types";
import { SubmitButtonColor } from "../../wizard/types";
import {
  COURSE_HOUSING_TYPES,
  COURSE_TYPES,
} from "../../language-course/constants";
import langs from "langs";

export const sumTuitionAndOtherFeesSupports: SumTuitionAndOtherFeesSupports = (
  tuition,
  hasTuitionSupport,
  tuitionWithSupport,
  otherFees,
  hasOtherFeesSupport,
  otherFeesWithSupport,
  currencyId,
) => ({
  tuitionOtherFeesSum: tuition + otherFees,
  tuitionOtherFeesSumWithCurrency: toCurrency(
    tuition + otherFees,
    currencyId,
    " ",
    "code",
  ),
  tuitionOtherFeesWithSupportsSum: tuitionWithSupport + otherFeesWithSupport,
  tuitionOtherFeesWithSupportsSumWithCurrency: toCurrency(
    tuitionWithSupport + otherFeesWithSupport,
    currencyId,
    " ",
    "code",
  ),
  hasFeesSupport: hasTuitionSupport || hasOtherFeesSupport,
});

export const calcAccommodationFeesAndTotalSum = (
  fees: IAccommodationFee[],
  total: number,
  totalWithSupport: number,
  currencyId: number,
  values: object,
): {
  calcTotalWithCurrency: string;
  calcTotalWithSupportWithCurrency: string;
} => {
  let calcTotal = total;
  let calcTotalWithSupport = totalWithSupport;

  fees.forEach((fee) => {
    if (values[fee.name]) {
      calcTotal += fee.amount;
      calcTotalWithSupport += fee.amount;
    }
  });

  return {
    calcTotalWithCurrency: toCurrency(calcTotal, currencyId, " ", "code"),
    calcTotalWithSupportWithCurrency: toCurrency(
      calcTotalWithSupport,
      currencyId,
      " ",
      "code",
    ),
  };
};

export const calcFeeWithScholarshipAndFinancialAid: CalcFeeWithScholarshipAndFinancialAid =
  (
    fee,
    hasFeeScholarship,
    feeScholarshipType,
    feeScholarshipAmount,
    hasFeeFinancialAid,
    feeFinancialAidType,
    feeFinancialAidAmount,
    currency,
  ) => {
    if (isNaN(fee)) {
      fee = 0;
    }

    const scholarship = calculateFinancialSupport(
      fee,
      hasFeeScholarship,
      feeScholarshipType,
      feeScholarshipAmount,
    );

    const financialAid = calculateFinancialSupport(
      fee,
      hasFeeFinancialAid,
      feeFinancialAidType,
      feeFinancialAidAmount,
    );

    let calcFee = fee;

    if (!isNaN(+scholarship)) {
      calcFee -= +scholarship;
    }

    if (!isNaN(+financialAid)) {
      calcFee -= +financialAid;
    }

    calcFee = calcFee < 0 ? 0 : calcFee;

    return {
      calcFee,
      hasSupport: calcFee < fee,
      feeWithCurrency: toCurrency(fee, currency, " ", "code"),
      calcFeeWithCurrency: toCurrency(calcFee, currency, " ", "code"),
      scholarshipWithCurrency: toCurrency(scholarship, currency, " ", "code"),
      financialAidWithCurrency: toCurrency(financialAid, currency, " ", "code"),
    };
  };

export const parseProgramListFilterOptionsToBackEndModel = (data) => {
  let filterOptions = {
    filterType: data?.filterType,
  } as any;

  if (data.filterType === FilterFormType.HIGHER && data?.higher) {
    if (data.higher.countries?.length) {
      filterOptions.countries = data.higher.countries
        .map((country) => country.code)
        ?.join();
    }

    if (data.higher.degree) {
      filterOptions.degreeId = data.higher.degree.id;
    }

    if (data.higher.major) {
      filterOptions.majorId = data.higher.major.id;
    }

    if (data.higher.programType) {
      filterOptions.programType = data.higher.programType.id;
    }

    if (data.higher.intake) {
      filterOptions.intake = data.higher.intake.id;
    }
  }

  if (data.filterType === FilterFormType.SECONDARY && data?.secondary) {
    if (data.secondary.countries?.length) {
      filterOptions.countries = data.secondary.countries
        .map((country) => country.code)
        ?.join();
    }

    if (data.secondary.schoolLevel) {
      filterOptions.levelId = data.secondary.schoolLevel.id;
    }

    if (data.secondary.schoolType) {
      filterOptions.schoolType = data.secondary.schoolType.id;
    }

    if (data.secondary.programType) {
      filterOptions.programType = data.secondary.programType.id;
    }

    if (data.secondary.intake) {
      filterOptions.intake = data.secondary.intake.id;
    }
  }

  if (data.filterType === FilterFormType.COURSE && data?.course) {
    if (data.course.courseType) {
      filterOptions.courseType = data.course.courseType.id;
    }

    if (data.course.countries?.length) {
      filterOptions.countries = data.course.countries
        .map((country) => country.code)
        ?.join();
    }

    if (data.course.language) {
      filterOptions.language = data.course.language.id;
    }

    if (data.course.housingType) {
      filterOptions.housingType = data.course.housingType.id;
    }
  }

  return filterOptions;
};

export const replaceForURLOptions = (data) => {
  let URLOptions = {
    filterType: data?.filterType,
  } as any;

  if (data?.higher) {
    URLOptions.higher = {};

    if (data.higher.countries?.length) {
      URLOptions.higher.countries = data.higher.countries
        .map((country) => country.code)
        ?.join();
    }

    if (data.higher.degree) {
      URLOptions.higher.degreeId = {
        id: data.higher.degree.id,
        name: data.higher.degree.name,
      };
    }

    if (data.higher.major) {
      URLOptions.higher.majorId = {
        id: data.higher.major.id,
        name: data.higher.major.name,
      };
    }

    if (data.higher.programType) {
      URLOptions.higher.programType = data.higher.programType.id;
    }

    if (data.higher.intake) {
      URLOptions.higher.intake = data.higher.intake.id;
    }
  }

  if (data?.secondary) {
    URLOptions.secondary = {};

    if (data.secondary.countries?.length) {
      URLOptions.secondary.countries = data.secondary.countries
        .map((country) => country.code)
        ?.join();
    }

    if (data.secondary.schoolLevel) {
      URLOptions.secondary.levelId = data.secondary.schoolLevel.id;
    }

    if (data.secondary.schoolType) {
      URLOptions.secondary.schoolType = data.secondary.schoolType.id;
    }

    if (data.secondary.programType) {
      URLOptions.secondary.programType = data.secondary.programType.id;
    }

    if (data.secondary.intake) {
      URLOptions.secondary.intake = data.secondary.intake.id;
    }
  }

  if (data?.course) {
    URLOptions.course = {};

    if (data.course.courseType) {
      URLOptions.course.courseType = data.course.courseType.id;
    }

    if (data.course.language) {
      URLOptions.course.language = data.course.language.id;
    }

    if (data.course.countries?.length) {
      URLOptions.course.countries = data.course.countries
        .map((country) => country.code)
        ?.join();
    }

    if (data.course.housingType) {
      URLOptions.course.housingType = data.course.housingType.id;
    }
  }

  return URLOptions;
};

export const replaceForFilterForm = (data) => {
  let filterForm = {
    filterType: data?.filterType,
  } as any;

  if (data?.higher) {
    filterForm.higher = {};

    if (data.higher?.countries) {
      const codes = data.higher.countries.split(",");
      filterForm.higher.countries = codes.map((code) => ({
        code,
        name: getName(code),
      }));
    }

    if (data.higher?.degreeId) {
      filterForm.higher.degree = {
        id: data.higher.degreeId.id,
        name: data.higher.degreeId.name,
      };
    }

    if (data.higher?.majorId) {
      filterForm.higher.major = {
        id: data.higher.majorId.id,
        name: data.higher.majorId.name,
      };
    }

    if (data.higher?.programType) {
      filterForm.higher.programType = {
        id: data.higher.programType,
        name: PROGRAM_TYPES.find((type) => type.id === data.higher.programType)!
          .name,
      };
    }

    if (data.higher?.intake) {
      filterForm.higher.intake = {
        id: data.higher.intake,
        name: INTAKES_MAP[data.higher.intake],
      };
    }
  }

  if (data?.secondary) {
    filterForm.secondary = {};

    if (data.secondary?.countries) {
      const codes = data.secondary.countries.split(",");
      filterForm.secondary.countries = codes.map((code) => ({
        code,
        name: getName(code),
      }));
    }

    if (data.secondary?.levelId) {
      filterForm.secondary.schoolLevel = {
        id: data.secondary.levelId,
        name: SECONDARY_SCHOOL_LEVEL.find(
          (level) => level.id === data.secondary.levelId,
        )!.name,
      };
    }

    if (data.secondary?.schoolType) {
      filterForm.secondary.schoolType = {
        id: data.secondary.schoolType,
        name: SECONDARY_SCHOOL_TYPE.find(
          (type) => type.id === data.secondary.schoolType,
        )!.name,
      };
    }

    if (data.secondary?.programType) {
      filterForm.secondary.programType = {
        id: data.secondary.programType,
        name: PROGRAM_TYPES.find(
          (type) => type.id === data.secondary.programType,
        )!.name,
      };
    }

    if (data.secondary?.intake) {
      filterForm.secondary.intake = {
        id: data.secondary.intake,
        name: INTAKES_MAP[data.secondary.intake],
      };
    }
  }

  if (data?.course) {
    filterForm.course = {};

    if (data.course?.courseType) {
      filterForm.course.courseType = {
        id: data.course.courseType,
        name: COURSE_TYPES.find((type) => type.id === data.course.courseType)!
          .name,
      };
    }

    if (data.course?.language) {
      filterForm.course.language = {
        id: data.course.language,
        name: langs.where("3", data.course.language)?.name,
      };
    }

    if (data.course?.countries) {
      const codes = data.course.countries.split(",");
      filterForm.course.countries = codes.map((code) => ({
        code,
        name: getName(code),
      }));
    }

    if (data.course?.housingType) {
      filterForm.course.housingType = {
        id: data.course.housingType,
        name: COURSE_HOUSING_TYPES.find(
          (type) => type.id === data.course.housingType,
        )!.name,
      };
    }
  }

  return filterForm;
};

export const beautifyRequiredDocuments = (docs: IRequiredDoc[]): string[] => {
  return docs.map((doc) => {
    const foundMainDoc = REQUIRED_DOCS.find((d) => d.name === doc.name);

    return foundMainDoc ? foundMainDoc.longName! : doc.name;
  });
};

export function getDeleteButtonConfigs(values) {
  const {
    injectedData: { approved, pending },
  } = values;

  if (approved + pending > 0) {
    return {
      submitButton: null,
      cancelButton: {
        text: "Close",
      },
    };
  }

  return {
    submitButton: {
      text: "Delete",
      color: SubmitButtonColor.Danger,
    },
    cancelButton: {
      text: "Close",
    },
  };
}
